import React from 'react'

import { colors } from '../tokens'

/**
 * Inline SVG glyphs traced from the design markup (Table-Option1.dc.html),
 * shared across the BenefitsTable subtree. Stroke uses `currentColor` so the
 * consuming element's `color` drives the glyph; the few that need a fixed tint
 * read it from `tokens.ts` directly (these run in JS, not CSS).
 */

const svgProps = (size: number, style?: React.CSSProperties) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  style: { width: size, height: size, ...style },
})

export const SearchSvg: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg {...svgProps(size)}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

export const FilterSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
  </svg>
)

export const CopySvg: React.FC<{ size?: number }> = ({ size = 11 }) => (
  <svg {...svgProps(size)}>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
)

/** Activity-log / history glyph (clock-with-arrow) — used by both the row
 * benefit-history button and the per-cell history indicator so they match. */
export const HistorySvg: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg {...svgProps(size)}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

export const TicketSvg: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg {...svgProps(size)}>
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
)

export const ColumnsSvg: React.FC<{ size?: number }> = ({ size = 13 }) => (
  <svg {...svgProps(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="9" y1="4" x2="9" y2="20" />
    <line x1="15" y1="4" x2="15" y2="20" />
  </svg>
)

export const ChevronLeftSvg: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg {...svgProps(size)}>
    <path d="m15 18-6-6 6-6" />
  </svg>
)

export const ChevronRightSvg: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg {...svgProps(size)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
)

/** Sortable-header chevrons; the active direction is tinted teal. */
export const SortSvg: React.FC<{ dir: 'asc' | 'desc' | null }> = ({ dir }) => (
  <svg {...svgProps(13, { color: colors.border.strong })}>
    <path
      d="m7 15 5 5 5-5"
      stroke={dir === 'desc' ? colors.brand.primary : undefined}
    />
    <path
      d="m7 9 5-5 5 5"
      stroke={dir === 'asc' ? colors.brand.primary : undefined}
    />
  </svg>
)

export const CheckboxOnSvg: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={18}
    height={18}
  >
    <rect
      x="2"
      y="2"
      width="20"
      height="20"
      rx="4"
      fill={colors.brand.primary}
    />
    <path
      d="m8 12 3 3 5-6"
      stroke="#fff"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
)

export const CheckboxOffSvg: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke={colors.text.faint}
    strokeWidth={2}
  >
    <rect x="2" y="2" width="20" height="20" rx="4" />
  </svg>
)

/** Jump-out arrow (arrow-up-right in a box) — a link that leaves this page. */
export const ExternalLinkSvg: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg {...svgProps(size)}>
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)
